/**
 * Part / revision / ids for audit trail rows — nested version_content + parent merge + action message fallback.
 * getCancelParams historically read only top-level version_content and missed procedure_number / to_part_rev on inner envelopes.
 * Bulk move/copy uploads (SECTION_MOVED, etc.) store partNumber/revision on new_content_snapshot JSON under version_content.
 */

import { parseSnapshotJson } from '../ActionComponents/utils/fetchSnapshotContent'

export function mergeAuditVersionContent(row) {
  const outer = row?.version_content
  if (!outer || typeof outer !== 'object' || Array.isArray(outer)) return null
  const inner = outer.version_content
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...outer, ...inner }
  }
  return outer
}

export function mergeAuditTrailRowVersionContentDeep(auditTrailRow) {
  if (!auditTrailRow || typeof auditTrailRow !== 'object') return null
  const child = mergeAuditVersionContent(auditTrailRow)
  const parentRow = auditTrailRow.parent_version_data
  const parent =
    parentRow && typeof parentRow === 'object'
      ? mergeAuditVersionContent(parentRow)
      : null
  if (parent && child) return { ...parent, ...child }
  return child || parent
}

/** Last resort: human audit lines often include "… in PART123, Rev A-" or "… from PART, Rev A". */
export function parsePartRevFromAuditActionMessage(msg) {
  if (!msg || typeof msg !== 'string') return { partNumber: '', revision: '' }
  const s = msg.trim()
  const patterns = [
    /\bfor\s+([^,]+?),\s*Rev\s+([^\s,.]+)/i,
    /\bin\s+([^,]+?),\s*Rev\s+([^\s,.]+)/i,
    /\bin\s+([^,]+?)\s+Rev\.?\s+([^\s,.]+)/i,
    /\bfrom\s+([^,]+?),\s*Rev\s+([^\s,.]+)/i,
    /,\s*in\s+([^,]+?),\s*Rev\s+([^\s,.]+)/i,
  ]
  for (const re of patterns) {
    const m = s.match(re)
    if (m) {
      return { partNumber: m[1].trim(), revision: m[2].trim() }
    }
  }
  return { partNumber: '', revision: '' }
}

/** Pick part + rev from a flat object (version_content, snapshot root, or parseSnapshotJson meta). */
/** Some snapshots store display labels like "item-code-390-563-6" — strip for Copy/cancel. */
export function normalizeAuditPartNumber(pn) {
  if (pn == null) return ''
  const s = String(pn).trim()
  if (!s) return ''
  const m = s.match(/^item-code-(.+)$/i)
  return m ? m[1].trim() : s
}

function pickPartRev(obj) {
  if (!obj || typeof obj !== 'object') return { partNumber: '', revision: '' }
  const pn =
    obj.partNumber ??
    obj.part_number ??
    obj.procedure_number ??
    obj.from_part_ref
  const rev =
    obj.revision ??
    obj.rev ??
    obj.part_rev ??
    obj.from_part_rev ??
    obj.to_part_rev ??
    obj.label
  return {
    partNumber:
      pn != null && String(pn).trim() !== '' ? String(pn).trim() : '',
    revision:
      rev != null && String(rev).trim() !== '' ? String(rev).trim() : '',
  }
}

function pickProcedureRevRef(obj) {
  if (!obj || typeof obj !== 'object') return undefined
  return (
    obj.procedure_rev_ref ||
    obj.revision_id_ref ||
    obj.from_procedure_rev_ref ||
    undefined
  )
}

/** Part UUID for get-procedure-nodes ?part_ref= */
function pickPartId(obj) {
  if (!obj || typeof obj !== 'object') return undefined
  const id =
    obj.part_id_ref ||
    obj.from_part_id_ref ||
    obj.part_ref_id ||
    obj.part_required_id_ref ||
    obj.part_ref ||
    undefined
  return id
}

/**
 * Reads procedure part/rev from parsed S3 snapshot JSON (single_audit_trail, bulk_audit_trail, UUID blocks, etc.).
 * MoveReplace uploads: { ...node_indexing, version_content: { partNumber, revision, ... } }.
 */
export function extractProcedureMetaFromSnapshotJson(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null

  if (data.version_content && typeof data.version_content === 'object') {
    const vc = data.version_content
    const m = pickPartRev(vc)
    const revisionId = pickProcedureRevRef(vc)
    const partId = pickPartId(vc)
    if (m.partNumber || m.revision || revisionId || partId) {
      return { ...m, revisionId, partId }
    }
  }

  const root = pickPartRev(data)
  const rootId = pickProcedureRevRef(data)
  const rootPartId = pickPartId(data)
  if (root.partNumber || root.revision || rootId || rootPartId) {
    return { ...root, revisionId: rootId, partId: rootPartId }
  }

  const parsed = parseSnapshotJson(data, 'new')
  if (parsed.meta) {
    const m = pickPartRev(parsed.meta)
    const mid = pickProcedureRevRef(parsed.meta)
    const mpid = pickPartId(parsed.meta)
    if (m.partNumber || m.revision || mid || mpid) {
      return { ...m, revisionId: mid, partId: mpid }
    }
  }

  return null
}

/**
 * Fallback: S3 key like item-code-390-563-6_A-SECTION_MOVED_1775221968899.json encodes part + rev.
 */
export function parsePartRevFromSnapshotUrl(url) {
  if (!url || typeof url !== 'string') return { partNumber: '', revision: '' }
  const name = decodeURIComponent(url.split('/').pop() || '').replace(/\.json$/i, '')
  const m = name.match(
    /item-code-(.+?)_([A-Za-z0-9+-]+)_(SECTION|STEP|SUBSTEP|VIDEO|ATTACHMENT|CANVAS|RICH|MOVE|COPY|ADD|EDIT|DELETE|MOVED|COPIED|CREATED|UPDATED|RICHTEXT|FORM)/i
  )
  if (m) {
    return { partNumber: m[1].trim(), revision: m[2].trim() }
  }
  return { partNumber: '', revision: '' }
}

/**
 * Fetches new_content_snapshot / previous_content_snapshot and merges part+rev into identity when API row is sparse.
 */
export async function enrichProcedureIdentityFromAuditSnapshots(audit, identity) {
  const out = {
    partNumber: identity.partNumber || '',
    revision: identity.revision || '',
    partId: identity.partId,
    revisionId: identity.revisionId,
  }

  const urls = [audit?.new_content_snapshot, audit?.previous_content_snapshot].filter(
    (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim())
  )

  for (const url of urls) {
    if (out.partNumber && out.revision && out.revisionId && out.partId) break
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      const data = await res.json()
      const meta = extractProcedureMetaFromSnapshotJson(data)
      if (meta) {
        if (!out.partNumber && meta.partNumber) out.partNumber = meta.partNumber
        if (!out.revision && meta.revision) out.revision = meta.revision
        if (!out.revisionId && meta.revisionId) out.revisionId = meta.revisionId
        if (!out.partId && meta.partId) out.partId = meta.partId
      }
    } catch (_) {
      /* CORS or network */
    }
  }

  if (!out.partNumber || !out.revision) {
    const fromName = parsePartRevFromSnapshotUrl(urls[0] || '')
    if (!out.partNumber && fromName.partNumber) out.partNumber = fromName.partNumber
    if (!out.revision && fromName.revision) out.revision = fromName.revision
  }

  return {
    ...identity,
    partNumber: normalizeAuditPartNumber(out.partNumber || identity.partNumber || ''),
    revision: out.revision || identity.revision || '',
    partId: out.partId || identity.partId,
    revisionId: out.revisionId || identity.revisionId,
  }
}

/**
 * Resolves procedure part number, revision, and ids for cancel / copy / AppBar.
 */
export function resolveAuditTrailProcedureIdentity(audit) {
  if (!audit || typeof audit !== 'object') {
    return {
      partNumber: '',
      revision: '',
      partId: undefined,
      revisionId: undefined,
    }
  }
  const root = audit.data && typeof audit.data === 'object' ? audit.data : audit
  const merged =
    mergeAuditTrailRowVersionContentDeep(audit) ||
    mergeAuditVersionContent(audit) ||
    {}
  const pr = root.procedure_revision || audit.procedure_revision

  const partId =
    merged.part_id_ref ||
    merged.part_ref_id ||
    merged.from_part_id_ref ||
    merged.part_required_id_ref ||
    merged.part_ref ||
    root.part_id_ref ||
    root.part_ref_id ||
    root.part_ref ||
    pr?.part_id_ref ||
    pr?.partId ||
    pr?.part_ref

  let revisionId =
    merged.procedure_rev_ref ||
    merged.revision_id_ref ||
    merged.from_procedure_rev_ref ||
    root.procedure_ref_id ||
    root.procedure_rev_ref ||
    root.revision_id_ref ||
    pr?.id ||
    pr?.procedure_revision_id ||
    pr?.revision_id

  let partNumber =
    merged.partNumber ??
    merged.part_number ??
    merged.procedure_number ??
    merged.from_part_ref ??
    pr?.part_number ??
    pr?.partNumber ??
    root.partNumber ??
    root.part_number ??
    audit.partNumber ??
    audit.part_number

  let revision =
    merged.revision ??
    merged.rev ??
    merged.part_rev ??
    merged.from_part_rev ??
    merged.to_part_rev ??
    pr?.revision ??
    pr?.rev ??
    pr?.part_rev ??
    pr?.label ??
    root.revision ??
    root.rev ??
    audit.revision ??
    audit.rev

  partNumber =
    partNumber != null && String(partNumber).trim() !== ''
      ? String(partNumber).trim()
      : ''
  revision =
    revision != null && String(revision).trim() !== ''
      ? String(revision).trim()
      : ''

  if (!partNumber || !revision) {
    const msg = typeof audit.action === 'string' ? audit.action : ''
    const parsed = parsePartRevFromAuditActionMessage(msg)
    if (!partNumber && parsed.partNumber) partNumber = parsed.partNumber
    if (!revision && parsed.revision) revision = parsed.revision
  }

  partNumber = normalizeAuditPartNumber(partNumber)

  // Do not use audit trail version_id as procedure_rev_ref — getRevAllNodes expects procedure revision id only.

  return { partNumber, revision, partId, revisionId }
}
